/**
 * Eros web wishlist (THEME-ADR-003).
 * Guest browser store only — Flutter uses its own Isar / cloud metafield.
 * Key: eros:wishlist → [{ handle, id? }, ...]
 *
 * Wave 22C / FS-019 — optional social aggregate (ADR-156 / ADR-157):
 * GET /v1/wishlist/aggregate/:productId + POST ping on toggle.
 * Fail closed (hide) on error; min display count 3 (Flutter SocialWishlistCountUseCase).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'eros:wishlist';
  var CHANGE_EVENT = 'eros:wishlist-change';
  var DEVICE_ID_KEY = 'eros_install_id_v1';
  var MIN_DISPLAY_COUNT = 3;
  var AGGREGATE_FETCHED = 'data-eros-wishlist-aggregate-bound';

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function commerceBaseUrl() {
    var vars = window.themeVariables || {};
    var settings = vars.settings || {};
    var base = settings.erosCommerceApiBase || '';
    if (window.ErosCommerce && typeof window.ErosCommerce.baseUrl === 'function') {
      try {
        base = window.ErosCommerce.baseUrl() || base;
      } catch (e) {
        /* ignore */
      }
    }
    if (!base || typeof base !== 'string') return '';
    return base.replace(/\/+$/, '');
  }

  function deviceId() {
    if (!window.localStorage) return 'inst_' + Date.now();
    try {
      var id = (window.localStorage.getItem(DEVICE_ID_KEY) || '').trim();
      if (!id) {
        id = 'inst_' + Date.now();
        window.localStorage.setItem(DEVICE_ID_KEY, id);
      }
      return id;
    } catch (e) {
      return 'inst_' + Date.now();
    }
  }

  function productGidFromEl(el) {
    if (!el) return '';
    var gid = (el.getAttribute('data-product-gid') || '').trim();
    if (gid.indexOf('gid://shopify/Product/') === 0) return gid;
    var nid = normalizeId(el.getAttribute('data-product-id'));
    if (nid) return 'gid://shopify/Product/' + nid;
    return '';
  }

  function formatSocialCopy(template, count) {
    if (!template) return String(count);
    return String(template)
      .replace(/__COUNT__/g, String(count))
      .replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  function socialCountElForToggle(btn) {
    if (!btn) return null;
    var actions = btn.closest('.eros-pdp-vendor-row__actions');
    if (actions) {
      return actions.querySelector('[data-eros-wishlist-social]');
    }
    return null;
  }

  function hideSocialCount(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
    el.setAttribute('aria-hidden', 'true');
  }

  function showSocialCount(el, count, template) {
    if (!el) return;
    el.setAttribute('data-eros-count', String(count));
    el.textContent = formatSocialCopy(template, count);
    el.hidden = false;
    el.removeAttribute('aria-hidden');
  }

  function fetchAggregateCount(productGid) {
    var base = commerceBaseUrl();
    if (!base || !productGid) return Promise.resolve(null);
    var url = base + '/v1/wishlist/aggregate/' + encodeURIComponent(productGid);
    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || data.ok === false) return null;
        var count = data.count;
        if (count == null) return null;
        var n = typeof count === 'number' ? count : parseInt(count, 10);
        if (!isFinite(n)) return null;
        return n;
      })
      .catch(function () {
        return null;
      });
  }

  function pingAggregate(productGid, added) {
    var base = commerceBaseUrl();
    if (!base || !productGid) return;
    fetch(base + '/v1/wishlist/aggregate/ping', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
      body: JSON.stringify({
        productId: productGid,
        deviceId: deviceId(),
        action: added ? 'add' : 'remove',
      }),
    }).catch(function () {
      /* fire-and-forget — CORS / network failures are OK (display-only still works) */
    });
  }

  function bindSocialAggregate(btn) {
    if (!btn || btn.getAttribute(AGGREGATE_FETCHED) === '1') return;
    var countEl = socialCountElForToggle(btn);
    if (!countEl) return;
    var productGid = productGidFromEl(btn);
    if (!productGid || !commerceBaseUrl()) {
      hideSocialCount(countEl);
      return;
    }
    btn.setAttribute(AGGREGATE_FETCHED, '1');
    var template = countEl.getAttribute('data-copy-template') || '';
    fetchAggregateCount(productGid).then(function (count) {
      if (count == null || count < MIN_DISPLAY_COUNT) {
        hideSocialCount(countEl);
        return;
      }
      showSocialCount(countEl, count, template);
    });
  }

  function bindAllSocialAggregates(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-eros-wishlist-toggle]').forEach(bindSocialAggregate);
  }

  function safeParse(raw) {
    try {
      var data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function readItems() {
    if (!window.localStorage) return [];
    return safeParse(window.localStorage.getItem(STORAGE_KEY)).filter(function (item) {
      return item && typeof item.handle === 'string' && item.handle.length > 0;
    });
  }

  function writeItems(items) {
    if (!window.localStorage) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* quota / private mode — fail soft */
    }
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items: items.slice() } })
    );
  }

  function normalizeId(id) {
    if (id == null || id === '') return null;
    var n = String(id).replace(/\D/g, '');
    return n || null;
  }

  function findIndex(items, handle, id) {
    var nid = normalizeId(id);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (nid && normalizeId(it.id) === nid) return i;
      if (handle && it.handle === handle) return i;
    }
    return -1;
  }

  function has(handle, id) {
    return findIndex(readItems(), handle, id) >= 0;
  }

  function add(handle, id) {
    if (!handle) return readItems();
    var items = readItems();
    if (findIndex(items, handle, id) >= 0) return items;
    items.push({ handle: handle, id: normalizeId(id) || undefined });
    writeItems(items);
    return items;
  }

  function remove(handle, id) {
    var items = readItems();
    var idx = findIndex(items, handle, id);
    if (idx < 0) return items;
    items.splice(idx, 1);
    writeItems(items);
    return items;
  }

  function toggle(handle, id) {
    if (has(handle, id)) {
      remove(handle, id);
      return false;
    }
    add(handle, id);
    return true;
  }

  function searchQuery(items) {
    return items
      .map(function (item) {
        var nid = normalizeId(item.id);
        if (nid) return 'id:' + nid;
        return 'handle:' + item.handle;
      })
      .join(' OR ');
  }

  function syncToggleEl(el) {
    if (!el) return;
    var handle = el.getAttribute('data-product-handle') || '';
    var id = el.getAttribute('data-product-id') || '';
    var active = has(handle, id);
    el.classList.toggle('is-active', active);
    el.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function syncAllToggles(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-eros-wishlist-toggle]').forEach(syncToggleEl);
  }

  function applyOptimisticSocialCount(countEl, delta) {
    if (!countEl || countEl.hidden) return null;
    var template = countEl.getAttribute('data-copy-template') || '';
    var current = parseInt(countEl.getAttribute('data-eros-count') || '', 10);
    if (!isFinite(current)) {
      var digits = (countEl.textContent || '').replace(/[^\d]/g, '');
      current = parseInt(digits, 10);
    }
    if (!isFinite(current)) return null;
    var next = Math.max(0, current + delta);
    countEl.setAttribute('data-eros-count', String(next));
    if (next < MIN_DISPLAY_COUNT) {
      hideSocialCount(countEl);
      return next;
    }
    showSocialCount(countEl, next, template);
    return next;
  }

  function refreshSocialAfterToggle(btn, added) {
    var countEl = socialCountElForToggle(btn);
    var productGid = productGidFromEl(btn);
    if (!productGid) return;
    pingAggregate(productGid, added);
    if (!countEl) return;

    var template = countEl.getAttribute('data-copy-template') || '';
    applyOptimisticSocialCount(countEl, added ? 1 : -1);

    fetchAggregateCount(productGid).then(function (count) {
      if (count == null || count < MIN_DISPLAY_COUNT) {
        hideSocialCount(countEl);
        countEl.removeAttribute('data-eros-count');
        return;
      }
      countEl.setAttribute('data-eros-count', String(count));
      showSocialCount(countEl, count, template);
    });
  }

  function onToggleClick(event) {
    var btn = event.target.closest('[data-eros-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    var handle = btn.getAttribute('data-product-handle') || '';
    var id = btn.getAttribute('data-product-id') || '';
    if (!handle) return;
    var added = toggle(handle, id);
    syncToggleEl(btn);
    refreshSocialAfterToggle(btn, added);
  }

  function extractSectionId(el) {
    var section = el.classList.contains('shopify-section')
      ? el
      : el.closest('.shopify-section');
    if (!section || !section.id) return '';
    return section.id.replace('shopify-section-', '');
  }

  function wireRemoveChips(host) {
    if (!host) return;
    host.querySelectorAll('[data-eros-wishlist-remove]').forEach(function (btn) {
      if (btn.dataset.erosBound) return;
      btn.dataset.erosBound = '1';
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var handle = btn.getAttribute('data-product-handle') || '';
        var id = btn.getAttribute('data-product-id') || '';
        remove(handle, id);
        loadWishlistPage();
      });
    });
  }

  function showPageState(root, state) {
    var loading = root.querySelector('[data-eros-wishlist-loading]');
    var empty = root.querySelector('[data-eros-wishlist-empty]');
    var host = root.querySelector('[data-eros-wishlist-host]');
    if (loading) {
      loading.hidden = state !== 'loading';
      if (state === 'loading') {
        loading.setAttribute('aria-busy', 'true');
      } else {
        loading.removeAttribute('aria-busy');
      }
    }
    if (empty) empty.hidden = state !== 'empty';
    if (host) host.hidden = state !== 'grid';
  }

  function loadWishlistPage() {
    var root = document.querySelector('[data-eros-wishlist]');
    if (!root) return;

    var host = root.querySelector('[data-eros-wishlist-host]');
    if (!host) return;

    var items = readItems();
    if (!items.length) {
      host.replaceChildren();
      showPageState(root, 'empty');
      return;
    }

    showPageState(root, 'loading');

    var sectionId =
      root.getAttribute('data-section-id') ||
      extractSectionId(root) ||
      'eros-wishlist';

    var q = searchQuery(items);
    var url =
      routesRoot() +
      'search?type=product&q=' +
      encodeURIComponent(q) +
      '&section_id=' +
      encodeURIComponent(sectionId);

    fetch(url, { priority: 'low' })
      .then(function (response) {
        if (!response.ok) throw new Error('fetch failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var remoteRoot =
          doc.querySelector('[data-eros-wishlist]') ||
          doc.querySelector('[data-eros-wishlist-host]');
        var remoteHost = remoteRoot
          ? remoteRoot.querySelector
            ? remoteRoot.querySelector('[data-eros-wishlist-host]') || remoteRoot
            : remoteRoot
          : null;

        if (!remoteHost) {
          showPageState(root, 'empty');
          return;
        }

        var grid = remoteHost.querySelector('[data-eros-wishlist-grid]');
        var cards = grid
          ? grid.querySelectorAll('product-card, .product-card')
          : remoteHost.querySelectorAll('product-card, .product-card');

        if (!cards || !cards.length) {
          host.replaceChildren();
          showPageState(root, 'empty');
          return;
        }

        host.replaceChildren();
        if (grid) {
          host.appendChild(document.importNode(grid, true));
        } else {
          var wrap = document.createElement('div');
          wrap.className = 'eros-wishlist__grid';
          wrap.setAttribute('data-eros-wishlist-grid', '');
          cards.forEach(function (card) {
            wrap.appendChild(document.importNode(card, true));
          });
          host.appendChild(wrap);
        }

        // Drop listing hearts; ensure remove chips are wired.
        host.querySelectorAll('[data-eros-wishlist-toggle]').forEach(function (el) {
          el.remove();
        });
        wireRemoveChips(host);
        showPageState(root, 'grid');

        // This grid is fetched+inserted well after page load, so any
        // script that only binds once at initial page load (e.g.
        // eros-plp-quick-add.js) never sees these new nodes — confirmed
        // live 2026-08-08 (wishlist quick-add button did nothing, same
        // control worked fine on the regular PLP grid). Generic event so
        // any such script can re-bind against freshly-inserted content.
        document.dispatchEvent(new CustomEvent('eros:content-loaded', { bubbles: true, detail: { root: host } }));
      })
      .catch(function () {
        host.replaceChildren();
        showPageState(root, 'empty');
      });
  }

  function init() {
    if (window.ErosWishlist && window.ErosWishlist._ready) {
      syncAllToggles(document);
      bindAllSocialAggregates(document);
      loadWishlistPage();
      return;
    }

    document.addEventListener('click', onToggleClick);
    document.addEventListener(CHANGE_EVENT, function () {
      syncAllToggles(document);
    });
    syncAllToggles(document);
    bindAllSocialAggregates(document);
    loadWishlistPage();

    window.ErosWishlist = {
      STORAGE_KEY: STORAGE_KEY,
      getItems: readItems,
      has: has,
      add: add,
      remove: remove,
      toggle: toggle,
      syncToggles: syncAllToggles,
      reloadPage: loadWishlistPage,
      bindSocialAggregates: bindAllSocialAggregates,
      _ready: true,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
